import React from 'react';
import { StarIcon, SparklesIcon, CheckCircleIcon, ShieldCheckIcon } from './Icons';

export default function Curriculum() {
  const skills = [
    {
      title: 'Counting & Subitizing',
      standard: 'CCSS K.CC.B.5',
      island: 'Counting Cove',
      desc: 'Building instant number perception through glowing fireflies, comparisons, and number line hopping instead of rote chanting.',
      icon: '✨'
    },
    {
      title: 'Shapes & Spatial Patterns',
      standard: 'CCSS K.G.A.2 & NCERT Gr 1',
      island: 'Shape Shore',
      desc: 'Identifying sides and corners with faint ghost guides, matching 2D/3D shapes, and completing playful repeating sequences.',
      icon: '📐'
    },
    {
      title: 'Hands-on Manipulative Addition',
      standard: 'CCSS 1.OA.C.6',
      island: 'Symbol Bridge',
      desc: 'Bridging numbers with wooden planks, ten-frames, and visual combining so the concept of sum is intuitively felt.',
      icon: '🪵'
    },
    {
      title: 'Telling Time & Daily Order',
      standard: 'CCSS 1.MD.B.3',
      island: 'Tick-Tock Tower',
      desc: 'Meeting Little Tick (hour hand) and Tock (minute hand) to master o’clock, half-past, and sequencing moments in the day.',
      icon: '⏰'
    },
    {
      title: 'Coins & Sharing Equally',
      standard: 'CCSS 2.MD.C.8',
      island: 'Market Meadow',
      desc: 'Paying market prices with dollar coins and sharing sweet mangoes equally among friends to understand division early.',
      icon: '🥭'
    },
    {
      title: 'Cultural Warmth & Multi-lingual Roots',
      standard: 'Early Heritage Learning',
      island: 'Home World',
      desc: 'Crafted with deep love for Telugu and Tamil speaking diaspora families, expanding to bilingual audio to preserve native tongue joys.',
      icon: '🌏'
    }
  ];

  return (
    <section id="curriculum" className="py-16 lg:py-24 bg-gradient-to-b from-white via-amber-50/40 to-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-16">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 font-heading font-bold text-xs uppercase tracking-wider">
            <CheckCircleIcon className="w-4 h-4 text-emerald-600" />
            <span>Curriculum &amp; Educational Pedagogy</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black text-slate-900 tracking-tight">
            Grounded in Research, Wrapped in Wonder
          </h2>
          <p className="text-slate-600 font-medium text-base sm:text-lg">
            We don’t believe in dry digital flashcards. We anchor early mathematical reasoning in concrete visuals, adaptive pacing, and emotional safety.
          </p>
        </div>

        {/* 6 Curriculum Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {skills.map((skill, index) => (
            <div
              key={index}
              className="rounded-3xl bg-white border-2 border-slate-200/80 p-6 sm:p-7 shadow-md hover:shadow-xl hover:border-kiki-yellow transition-all flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-3xl p-3 bg-slate-50 rounded-2xl border border-slate-100">
                    {skill.icon}
                  </span>
                  <span className="text-[11px] font-heading font-bold text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full">
                    {skill.standard}
                  </span>
                </div>
                <span className="text-xs font-bold text-kiki-blue uppercase tracking-wider">
                  {skill.island}
                </span>
                <h3 className="font-heading font-bold text-xl text-slate-900 mt-1 mb-2">
                  {skill.title}
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 font-medium leading-relaxed">
                  {skill.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* The Zero-Failure Guarantee Feature Box */}
        <div className="mt-16 rounded-[36px] bg-white border-4 border-kiki-green/30 p-8 sm:p-10 shadow-xl max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8 space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-heading font-bold">
                <ShieldCheckIcon className="w-4 h-4 text-emerald-700" />
                <span>The Miss Kiki Promise</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-heading font-black text-slate-900">
                Our "No-Tears" Zero-Failure System
              </h3>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-medium">
                Standard games buzz loudly when a child taps the wrong answer, creating performance anxiety. 
                In Kiki &amp; Kids, there is no buzzer. If a child misses, Miss Kiki provides an encouraging spoken hint. 
                If they miss again, the correct answer glows gently so they celebrate learning without frustration.
              </p>
            </div>
            <div className="md:col-span-4 flex justify-center">
              <div className="w-40 h-40 rounded-3xl bg-emerald-50 border-2 border-emerald-200 p-4 flex flex-col items-center justify-center text-center shadow-inner">
                <span className="text-4xl mb-1">🌱</span>
                <span className="font-heading font-bold text-emerald-900 text-sm">Gentle Hints</span>
                <span className="text-[11px] text-emerald-700 font-medium">Never a buzzer</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
